import { AppIcon } from '@/components/ui/app-icon';
import { PRIMARY_BUTTON } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useUser } from '@clerk/react';
import { useEffect, useRef, useState } from 'react';
import { iconArrowUpRight, iconCircleCheckSolid } from '@/lib/icons';
import { BRAND } from '@/lib/brand';

/**
 * The shared Cleanlist team calendar, and the fallback for any seat band that
 * has no calendar of its own yet.
 *
 * This is a direct *event* link, not the `/d/...` routing form the other
 * "book a call" surfaces still use. That distinction is the whole point: a
 * direct link opens on "Select a Date & Time" and only asks for name and email
 * after a slot is chosen, and it honours `?name=`/`?email=` prefill. The
 * routing form does neither - it front-loads six questions, and its fields are
 * UUID-named so nothing can be prefilled into them.
 */
export const CLEANLIST_TEAM_CALL_URL = 'https://calendly.com/cleanlist/30min';

/** The founder-led call - Sameer's own calendar, so it carries his
 *  availability rather than the team pool's. */
const FOUNDER_CALL_URL = 'https://calendly.com/sameer-cleanlist/30min';

/**
 * Which calendar each seat band books into.
 *
 * 1-3 seats is the founder-led motion; 4+ goes to the shared team calendar.
 * Re-routing a band is a one-line edit here - nothing else in the flow knows
 * which calendar it is showing. A link only belongs in this map if that
 * calendar's Calendly availability is real, since availability is per-calendar.
 */
const CALENDAR_BY_SEATS: Record<string, string> = {
  '1_3': FOUNDER_CALL_URL,
  '4_10': CLEANLIST_TEAM_CALL_URL,
  '11_20': CLEANLIST_TEAM_CALL_URL,
  '21_plus': CLEANLIST_TEAM_CALL_URL,
};

/**
 * Signup domains that take the founder-led call whatever the seat band says.
 *
 * A personal inbox is the one signal that outranks seats: seats is a
 * self-reported answer on an optional question, while the domain is a fact we
 * hold at signup. Someone booking from a personal address is a solo operator
 * or a very early team no matter which band they clicked, and that is the
 * founder-led motion. Add a domain here to route it the same way.
 */
const FOUNDER_CALL_EMAIL_DOMAINS = new Set(['gmail.com']);

function emailDomain(email: string | undefined): string | undefined {
  if (!email) return undefined;
  const [, domain] = email.trim().toLowerCase().split('@');
  return domain || undefined;
}

/**
 * Territories that take the founder-led call whatever the seat band says.
 *
 * Timezone rather than IP on purpose. India is a single timezone, so this is an
 * unusually clean test for it, and a device timezone survives the VPN that
 * would defeat an IP lookup. Both spellings are listed because the IANA
 * database carries Asia/Calcutta as a legacy alias and older browsers still
 * report it. Asia/Colombo is Sri Lanka on the same offset and is deliberately
 * absent: the ask was India.
 */
const FOUNDER_CALL_TIME_ZONES = new Set(['Asia/Kolkata', 'Asia/Calcutta']);

/** The browser's IANA timezone, or "" where it cannot be read. */
function resolveTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone ?? '';
  } catch {
    return '';
  }
}

/**
 * Seats is an optional question, so an unanswered or unknown band is normal.
 * Those fall through to the shared team calendar rather than blocking the
 * booking - a skipped question must never cost us the call.
 *
 * Two signals outrank the band, and both point the same way. A founder-call
 * domain wins over every band including 21+, and so does a founder-call
 * territory: Lev asked for India to reach Sameer, and a 21-seat account in
 * Delhi is still India.
 *
 * `timeZone` is a parameter with a live default so the routing stays testable
 * without stubbing Intl.
 */
export function calendarUrlForSeats(
  seats: string | undefined,
  email?: string,
  timeZone: string = resolveTimeZone()
): string {
  const domain = emailDomain(email);
  if (domain && FOUNDER_CALL_EMAIL_DOMAINS.has(domain)) {
    return FOUNDER_CALL_URL;
  }
  if (FOUNDER_CALL_TIME_ZONES.has(timeZone)) {
    return FOUNDER_CALL_URL;
  }
  if (!seats) return CLEANLIST_TEAM_CALL_URL;
  return CALENDAR_BY_SEATS[seats] ?? CLEANLIST_TEAM_CALL_URL;
}

/** The calendar of the host the onboarding setup run routed the call to. */
export function calendarUrlForHost(host: 'lev' | 'sameer'): string {
  return host === 'sameer' ? FOUNDER_CALL_URL : CLEANLIST_TEAM_CALL_URL;
}

/** Slug → the label a human reads. These values land in the Slack booking
 *  alert via Calendly's utm passthrough, so they ship as display text rather
 *  than as the raw onboarding slugs. */
const SEAT_LABELS: Record<string, string> = {
  '1_3': '1-3',
  '4_10': '4-10',
  '11_20': '11-20',
  '21_plus': '21+',
};

const REFERRAL_LABELS: Record<string, string> = {
  google_search: 'Google Search',
  chatgpt_ai: 'ChatGPT / AI Assistant',
  linkedin: 'LinkedIn',
  referral_word_of_mouth: 'Referral / Word of mouth',
  appsumo: 'Appsumo',
  other: 'Other',
};

export interface BookingContext {
  name?: string;
  email?: string;
  /** Seat band, e.g. '1_3'. */
  seats?: string;
  /** Attribution answer, e.g. 'linkedin'. */
  referralSource?: string;
  /** Which surface sent them, shown as "Page" in the booking alert. */
  surface?: string;
}

/**
 * Builds the embed URL.
 *
 * Encoding is hand-rolled rather than URLSearchParams on purpose:
 * URLSearchParams renders a space as `+`, and Calendly does not decode that
 * back to a space — it prefills the literal "Victor+Paraschiv". Percent
 * encoding is what the live booking page actually accepts.
 *
 * Seats and referral ride along as UTM params because Calendly passes those
 * through into `payload.tracking` on the booking webhook. That is what puts
 * them back in the #demonstration-bookings alert: the routing form used to
 * supply that context, and going direct-to-calendar removed it.
 */
export function buildBookingUrl(
  calendarUrl: string,
  ctx: BookingContext
): string {
  const pairs: [string, string][] = [
    ['hide_event_type_details', '1'],
    ['hide_gdpr_banner', '1'],
  ];
  if (ctx.name) pairs.push(['name', ctx.name]);
  if (ctx.email) pairs.push(['email', ctx.email]);
  if (ctx.seats) {
    pairs.push(['utm_content', SEAT_LABELS[ctx.seats] ?? ctx.seats]);
  }
  if (ctx.referralSource) {
    pairs.push([
      'utm_term',
      REFERRAL_LABELS[ctx.referralSource] ?? ctx.referralSource,
    ]);
  }
  pairs.push(['utm_source', ctx.surface ?? 'Onboarding']);

  const qs = pairs
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join('&');
  return `${calendarUrl}?${qs}`;
}

const CALENDLY_WIDGET_SRC =
  'https://assets.calendly.com/assets/external/widget.js';
const CALENDLY_ORIGIN = 'https://calendly.com';

/** If the widget hasn't rendered by now, assume it never will (offline, an ad
 *  blocker, a Calendly outage) and fall back to the link-out CTA. The step must
 *  never dead-end on a third party. */
const CALENDLY_LOAD_TIMEOUT_MS = 6000;

/** The embed sizes itself from Calendly's `page_height` events. This floor
 *  stops the panel collapsing during the early small measurements Calendly
 *  emits while the form paints (26px, then 124px, then the real height). */
const MIN_EMBED_HEIGHT_PX = 640;

declare global {
  interface Window {
    Calendly?: {
      initInlineWidget(options: {
        url: string;
        parentElement: HTMLElement;
      }): void;
    };
  }
}

/** Loads widget.js once per page, no matter how many times the step mounts. */
let calendlyScriptPromise: Promise<void> | null = null;
function loadCalendlyScript(): Promise<void> {
  if (window.Calendly) return Promise.resolve();
  if (calendlyScriptPromise) return calendlyScriptPromise;

  calendlyScriptPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${CALENDLY_WIDGET_SRC}"]`
    );
    const script = existing ?? document.createElement('script');
    script.addEventListener('load', () => resolve());
    script.addEventListener('error', () => {
      // Let a later mount retry rather than caching the failure forever.
      calendlyScriptPromise = null;
      reject(new Error('Calendly widget failed to load'));
    });
    if (!existing) {
      script.src = CALENDLY_WIDGET_SRC;
      script.async = true;
      document.body.appendChild(script);
    }
  });

  return calendlyScriptPromise;
}

interface CalendlyMessage {
  event: string;
  /** `page_height` carries a CSS length, e.g. `{ height: '1098px' }`. */
  payload?: { height?: string };
}

function isCalendlyEvent(e: MessageEvent): e is MessageEvent<CalendlyMessage> {
  return (
    e.origin === CALENDLY_ORIGIN &&
    typeof e.data === 'object' &&
    e.data !== null &&
    typeof (e.data as CalendlyMessage).event === 'string' &&
    (e.data as CalendlyMessage).event.startsWith('calendly.')
  );
}

interface OnboardingBookingStepProps {
  /** A confirmed booking - Calendly's `event_scheduled`, not a click. */
  onBooked: () => void;
  /** Finishes onboarding and lands the user in the product. */
  onContinue: () => void;
  /**
   * The link-out path, used only when the embed can't render. Opening Calendly
   * in a new tab also completes onboarding, so the product stays reachable.
   */
  onFallbackBook: () => void;
  /** True once Calendly has confirmed a booking; owned by the parent so the
   *  card header can change with the body. */
  booked: boolean;
  /** The calendar this user's seat band routes to (see calendarUrlForSeats). */
  calendarUrl: string;
  /** Seat band + attribution, forwarded to Calendly so the booking alert keeps
   *  the context the routing form used to carry. */
  seats?: string;
  referralSource?: string;
  /** Suppresses input while onboarding completion is in flight. */
  disabled?: boolean;
}

/**
 * The final onboarding step: book the onboarding call, in place.
 *
 * The calendar is embedded rather than linked so nobody has to leave the flow
 * to book - the same reason the product-demo step before it plays its videos
 * inline. Embedding also buys a real completion signal: Calendly posts
 * `event_scheduled` back to the parent window, so we can tell a booking from a
 * click, which a link-out never could.
 *
 * The offer copy lives in the card header, like every other step - the body is
 * given over entirely to the calendar so the card stays a sane height.
 *
 * The escape hatches (Skip, and the X in the card corner) are owned by the
 * parent so it can reveal them together on its own timer - the call is
 * optional, and the product is never gated behind it.
 */
export function OnboardingBookingStep({
  onBooked,
  onContinue,
  onFallbackBook,
  booked,
  calendarUrl,
  seats,
  referralSource,
  disabled = false,
}: OnboardingBookingStepProps) {
  const { user } = useUser();
  const [mounted, setMounted] = useState(false);
  const [embedFailed, setEmbedFailed] = useState(false);
  const [embedHeight, setEmbedHeight] = useState(MIN_EMBED_HEIGHT_PX);
  const embedRef = useRef<HTMLDivElement>(null);
  // Read inside the listener without re-subscribing when the parent re-renders.
  const onBookedRef = useRef(onBooked);
  onBookedRef.current = onBooked;

  useEffect(() => {
    requestAnimationFrame(() => setMounted(true));
  }, []);

  // Prefill from Clerk so the only screen after picking a slot arrives filled
  // in. Verified working on the direct event link.
  const prefillName = user?.fullName ?? undefined;
  const prefillEmail = user?.primaryEmailAddress?.emailAddress ?? undefined;

  useEffect(() => {
    if (booked) return;
    const parentElement = embedRef.current;
    if (!parentElement) return;

    let cancelled = false;
    const timeoutId = setTimeout(() => {
      if (!cancelled) setEmbedFailed(true);
    }, CALENDLY_LOAD_TIMEOUT_MS);

    loadCalendlyScript()
      .then(() => {
        if (cancelled || !window.Calendly) return;
        clearTimeout(timeoutId);
        // initInlineWidget adds the embed_domain / embed_type params that make
        // Calendly post its events back to us - a hand-rolled iframe would not.
        window.Calendly.initInlineWidget({
          url: buildBookingUrl(calendarUrl, {
            name: prefillName,
            email: prefillEmail,
            seats,
            referralSource,
          }),
          parentElement,
        });
      })
      .catch(() => {
        if (!cancelled) setEmbedFailed(true);
      });

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
      parentElement.innerHTML = '';
    };
  }, [booked, calendarUrl, prefillName, prefillEmail, seats, referralSource]);

  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      if (!isCalendlyEvent(e)) return;
      if (e.data.event === 'calendly.event_scheduled') {
        onBookedRef.current();
        return;
      }
      if (e.data.event === 'calendly.page_height') {
        const reported = Number.parseInt(e.data.payload?.height ?? '', 10);
        if (Number.isFinite(reported) && reported > 0) {
          setEmbedHeight(Math.max(MIN_EMBED_HEIGHT_PX, reported));
        }
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  return (
    <div
      className={cn(
        'transition-all duration-500',
        mounted ? 'translate-y-0 opacity-100' : 'translate-y-3 opacity-0'
      )}
    >
      {booked ? (
        // The confirmation owns its whole panel. It used to keep the headline
        // up in the card header, which left a bordered box empty but for a
        // tick and a button - it read as a rendering fault, not a success.
        <div
          className={cn(
            'rounded-2xl border border-border/60 px-6 py-12 text-center sm:px-10',
            'bg-[radial-gradient(ellipse_at_top,rgba(16,185,129,0.10)_0%,transparent_60%)]',
            'dark:border-white/8 dark:bg-[radial-gradient(ellipse_at_top,rgba(16,185,129,0.09)_0%,transparent_60%)]'
          )}
        >
          <h2 className='flex items-center justify-center gap-2.5 text-xl font-semibold tracking-tight text-foreground sm:text-2xl'>
            <AppIcon
              icon={iconCircleCheckSolid}
              className='text-emerald-500'
              style={{ fontSize: 20 }}
            />
            You&rsquo;re booked!
          </h2>
          <p className='mx-auto mt-3 max-w-sm text-sm leading-relaxed text-muted-foreground'>
            {prefillEmail ? (
              <>
                The calendar invite is on its way to{' '}
                <span className='font-medium text-foreground'>
                  {prefillEmail}
                </span>
                .
              </>
            ) : (
              'The calendar invite is on its way to your inbox.'
            )}
          </p>

          {/* What actually happens next, in the order it happens. */}
          <ul className='mx-auto mt-8 max-w-sm space-y-2.5 text-left'>
            {[
              'Check your inbox for the invite and add it to your calendar.',
              'We spend 30 minutes launching your first workflow together.',
              'Your 500 credits are added after the call.',
            ].map((line, i) => (
              <li
                key={line}
                className='flex gap-3 text-sm leading-relaxed text-muted-foreground'
              >
                <span className='mt-px font-semibold text-primary tabular-nums'>
                  {i + 1}
                </span>
                <span>{line}</span>
              </li>
            ))}
          </ul>

          <button
            type='button'
            onClick={onContinue}
            disabled={disabled}
            className={cn(
              PRIMARY_BUTTON,
              'mt-9 inline-flex h-11 w-full max-w-xs items-center justify-center gap-2 px-6',
              'text-sm font-semibold',
              'focus-visible:ring-1 focus-visible:ring-ring focus-visible:outline-hidden',
              'disabled:opacity-50'
            )}
          >
            Start using {BRAND.name}
          </button>
        </div>
      ) : embedFailed ? (
        // The calendar couldn't render. Rather than dead-end, offer the tab.
        <div
          className={cn(
            'rounded-2xl border border-border/60 px-6 py-10 text-center sm:px-10',
            'bg-[radial-gradient(ellipse_at_top,rgba(235,180,145,0.20)_0%,transparent_60%),radial-gradient(ellipse_at_bottom_right,rgba(195,170,220,0.18)_0%,transparent_55%)]',
            'dark:border-white/8 dark:bg-[radial-gradient(ellipse_at_top,rgba(235,180,145,0.07)_0%,transparent_60%),radial-gradient(ellipse_at_bottom_right,rgba(195,170,220,0.06)_0%,transparent_55%)]'
          )}
        >
          <p className='mx-auto max-w-sm text-sm leading-relaxed text-muted-foreground'>
            The calendar didn't load here. Booking still works in a new tab.
          </p>
          <a
            href={buildBookingUrl(calendarUrl, {
              name: prefillName,
              email: prefillEmail,
              seats,
              referralSource,
            })}
            target='_blank'
            rel='noopener noreferrer'
            onClick={onFallbackBook}
            aria-disabled={disabled || undefined}
            className={cn(
              PRIMARY_BUTTON,
              'group mt-6 inline-flex h-11 w-full max-w-xs items-center justify-center gap-2 px-6',
              'text-sm font-semibold',
              'focus-visible:ring-1 focus-visible:ring-ring focus-visible:outline-hidden',
              disabled && 'pointer-events-none opacity-50'
            )}
          >
            Book your onboarding
            <AppIcon
              icon={iconArrowUpRight}
              className='transition-transform duration-200 group-hover:translate-x-px group-hover:-translate-y-px motion-reduce:transition-none'
              style={{ fontSize: 13 }}
            />
          </a>
        </div>
      ) : (
        // Calendly renders light in either theme, so the panel stays white on
        // purpose - a dark surround with a white iframe reads as a mistake.
        <div className='overflow-hidden rounded-2xl border border-border/60 bg-white dark:border-white/8'>
          <div
            ref={embedRef}
            className='w-full'
            style={{ height: embedHeight }}
          />
        </div>
      )}
    </div>
  );
}
